import every from 'lodash/every';
import filter from 'lodash/filter';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import remove from 'lodash/remove';
import without from 'lodash/without';
import isEqualLd from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import isEmpty from 'lodash/isEmpty';

const identity = v => v;

export function checkInBoundIndex(index, length) {
  return index >= 0 && index < length;
}

export function checkInBoundMap(indexMap, length) {
  return Object.entries(indexMap)
    .filter(([, index]) => !checkInBoundIndex(index, length))
    .reduce((a, [name, index]) => ({ ...a, [name]: index }), {});
}

/*
 * Remove all items from list `b` (arrToRemove) from list `a` (arr).
 *
 * Way faster for large arrays than `lodash/difference`. Basically theirs does nested loops (N * M) and more extensive comparisons.
 * This one is (N + M) assuming Set can check in somewhat constant time, and only does strict equality checks.
 *
 * For lodash implementation details see source:
 * https://github.com/lodash/lodash/blob/4.17.15/lodash.js#L2764 (`function baseDifference(array, values, iteratee, comparator)`)
 */
export const difference = (arr, arrToRemove) => {
  const toRemoveSet = new Set(arrToRemove);
  return arr.filter(v => !toRemoveSet.has(v));
};

/**
 * Compare 2 arrays and see if their contents are the same. This only works for elements of simple data types
 * (using === compares the content instead of reference)
 * Elements are not required to be sorted in either array; they'll be copied & sorted so original arrays won't be mutated.
 *
 * Will return false if either parameter is not provided or not an array.
 * Will throw an error if element data type is not scalar.
 * @param {[string|number|boolean|undefined|null]} array1
 * @param {[string|number|boolean|undefined|null]} array2
 * @returns {boolean} true if contents are equal; false if contents are not.
 */
export function isEqual(array1, array2) {
  if (!array1 || !Array.isArray(array1) || !array2 || !Array.isArray(array2)) {
    return false;
  }

  if (array1.length !== array2.length) return false;

  const isElementAccepted = element =>
    // typeof null is 'object' so can't put that here.
    ['string', 'number', 'boolean', 'undefined'].includes(typeof element) ||
    element === null;

  const throwError = (arrayName, value) => {
    throw new Error(
      `${arrayName} contains non scalar value ${value} which cannot be compared`,
    );
  };

  // Sort both arrays before comparison, so we use nlog(n) time instead of n^2 time.
  const array1Sorted = [...array1].sort();
  const array2Sorted = [...array2].sort();

  return array1Sorted.every((element, i) => {
    // First, make sure data types are correct. Then, just do array1[i] === array2[i] because arrays are sorted.
    const elementFrom2 = array2Sorted[i];
    if (!isElementAccepted(element)) {
      throwError('array1', element);
    }
    if (!isElementAccepted(elementFrom2)) {
      throwError('array2', elementFrom2);
    }

    return element === elementFrom2;
  });
}

export function containSameElements(arrA, arrB, comparisonFnArg) {
  const comparisonFn = comparisonFnArg ?? (item => item?.id);
  const arrAComparableSet = new Set(map(arrA, comparisonFn));
  if (!every(arrB, item => arrAComparableSet.has(comparisonFn(item)))) {
    return false;
  }
  const arrBComparableSet = new Set(map(arrB, comparisonFn));
  if (!every(arrA, item => arrBComparableSet.has(comparisonFn(item)))) {
    return false;
  }
  return true;
}

export function getHighlightedTextPositions(text, searchQuery) {
  // Ensure searchQuery and text exist, and that text is a String (or at least has a toLowerCase to make a String):
  if (!searchQuery || !text?.toLowerCase) {
    return {
      highlightStart: 0,
      highlightEnd: 0,
    };
  }

  const highlightStart = text.toLowerCase().indexOf(searchQuery);
  if (highlightStart > -1) {
    return {
      highlightStart,
      highlightEnd: highlightStart + searchQuery.length,
    };
  }
  return {
    highlightStart: 0,
    highlightEnd: 0,
  };
}

/*
 * TODO: consider other options to improve memory usage.
 *   Ideas:
 *    - allow passing `opts` to only find the first N to reduce memory usage:
 *    - have a similar method that simply returns an array of int indexes (if going to have ~1 million items, might as well keep copies small)
 */
export function search(collection, query = '') {
  const queryLower = query.toLowerCase();
  return reduce(
    collection,
    (results, text, i) => {
      if (text) {
        const highlightPositions = getHighlightedTextPositions(
          text,
          queryLower,
        );
        if (highlightPositions.highlightEnd > 0) {
          // Modifying original results Array instead of `return [...results, thing]` since this is intended to be used with extremely large collections:
          results.push({
            index: i,
            text,
            ...highlightPositions,
          });
        }
      }
      return results;
    },
    [],
  );
}
export function simpleSearchFilter(collection, query = '', opts = {}) {
  const queryLower = query.toLowerCase();
  const textGetter = opts.textGetter || identity;
  return filter(
    collection,
    text => textGetter(text).toLowerCase().indexOf(queryLower) > -1,
  );
}

// Return items in a collection that match all queries:
export function simpleSearchFilterAll(collection, queries, opts) {
  const meaningfulQueries = filter(queries, q => q); // Remove blanks, nulls, undefineds, etc.
  return reduce(
    meaningfulQueries,
    (arr, query) => simpleSearchFilter(arr, query, opts),
    collection,
  );
}

/**
 * Pluck out elements from the array, and put them at the back. If more than 1 elements are plucked,
 * their "before plucked" order is maintained.
 * Will not mutate original array; will return a new one.
 * Example: pluckAndAppend([1, 2, 3, 4, 5], num => num < 4) would result [4, 5, 1, 2, 3].
 * @param {[any]} array - The array we want to work with. Won't be mutated.
 * @param {function} predicate - The function that returns true if the element is to be put at the back; false if the element is to remain in place.
 * @return {[any]} - The "sorted" array where elements are taken out and put at the back according to predicate.
 */
export function pluckAndAppend(array, predicate) {
  if (!predicate || typeof predicate !== 'function') {
    return array;
  }

  const copy = [...array];
  const plucked = remove(copy, predicate);

  return [...copy, ...plucked];
}

const getOrDefault = (mapObj, key, defaultValue) => {
  if (!mapObj.has(key)) {
    mapObj.set(key, defaultValue);
  }
  return mapObj.get(key);
};

/**
 * buildDependencyMaps is a utility for creating bidirectional dependency Maps.
 * The dependsOn Map will contain values of prerequisites that need to be solved before the current key;
 * The isDependedOnBy Map is essentially inverted, where a given key
 * references all items that consider it a prerequisite.
 *
 * @param {[any]} items
 * @param {function} getPrerequisiteIds
 * @param {{getId: function}} getPrerequisiteIds
 * @returns {{dependsOn: Map, isDependedOnBy: Map}}
 */
export function buildDependencyMaps(items, getPrerequisiteIds, opts = {}) {
  /**
   * getId can return any unique identifier for the element, including the element itself (e.g. if using Strings).
   * Often it is will be an item Object's UUID or similar.
   * Since this method uses Map structures, that is not technically necessary,
   * but can alleviate problems if you have multiple copies of the same Object in your collection.
   */
  const getId = opts?.getId ?? identity;
  const dependsOn = new Map();
  const isDependedOnBy = new Map();

  items?.forEach(item => {
    const itemId = getId(item);
    const itemDependsOn = getOrDefault(dependsOn, itemId, []);
    getOrDefault(isDependedOnBy, itemId, []);

    getPrerequisiteIds(item)?.forEach(prereqId => {
      itemDependsOn.push(prereqId);
      getOrDefault(isDependedOnBy, prereqId, []).push(itemId);
      // Ensure dependsOn has all items; this helps patch gaps when `items` is incomplete:
      getOrDefault(dependsOn, prereqId, []);
    });
  });

  return {
    dependsOn,
    isDependedOnBy,
  };
}

/**
 * SHORT_CIRCUIT is an Object that, when returned in an iterator function,
 * indicates that the function should cease running. This is used for performance improvements on large graphs.
 *
 * It uses a constant Object, rather than a String, Number or other type simply to avoid mistakes (e.g. instead of
 * accidentally returning 0 from a generic function, the programmer must import the specific reference in order to
 * cause a short-circuit exit.)
 */
export const SHORT_CIRCUIT = { SHORT_CIRCUIT: 'SHORT_CIRCUIT' };

/**
 * iterateTopologically is a utility to iterate elements that have prerequisites in an
 * order where all prerequisites are handled before the items that depend on them.
 *
 * For example, in the following graph:
 * a -> b -> c
 * z -> b
 *
 * It would run a, z, b, c
 * (the order a and z run is undefined, but b would run after both, and finally c.)
 *
 * This is most easily used in conjunction with buildDependencyMaps.
 *
 * Important notes:
 * 1. iterateTopologically MUTATES!
 *    For performance this doesn't deep-copy the dependency objects by default, if you plan
 *    to reuse `dependsOn` or `isDependedOnBy`, make deep copies to pass this method yourself.
 * 2. iterateTopologically does not currently handle cycles.
 *    Specifically, it neither enters a cycle nor throws an error, it simply never calls
 *    runOnItem for items that exist in a cycle, not resolves their dependencies.
 *
 * @param {Map} dependsOn - Map of dependencies by dependant
 * @param {Map} isDependedOnBy - Inverted dependsOn Map (map of dependants by dependency)
 * @param {function} runOnItem - Method to run on each item whose prerequisites are all met
 * @param {function} runOnDependent - Method to run on each prerequisite resolution event
 * @returns {Void} This function doesn't return anything, runOnItem and runOnDependent are its purpose.
 *
 */
export function iterateTopologically({
  dependsOn,
  isDependedOnBy,
  runOnItem,
  runOnDependent,
}) {
  // stack will always only contain only the items where all prerequisites are resolved:
  const isBlocked = item => dependsOn.get(item)?.length;
  const stack = Array.from(dependsOn.keys()).filter(item => !isBlocked(item));
  const hasHandled = new Set();

  while (stack.length) {
    const nextItem = stack.pop();
    if (runOnItem?.(nextItem) === SHORT_CIRCUIT) {
      return;
    }

    if (isDependedOnBy.has(nextItem)) {
      const itemDependents = isDependedOnBy.get(nextItem);
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < itemDependents.length; i++) {
        const dependent = itemDependents[i];
        if (runOnDependent?.(nextItem, dependent) === SHORT_CIRCUIT) {
          return;
        }
        // Remove references to nextItem from `dependsOn` (mark the dependency resolved):
        dependsOn.set(dependent, without(dependsOn.get(dependent), nextItem));

        // If all dependencies are resolved and we have never been sent to runFunction, allow it to be run:
        if (!isBlocked(dependent) && !hasHandled.has(dependent)) {
          stack.push(dependent);
          // Infinite-loop prevention for cycles:
          hasHandled.add(dependent);
        }
      }
    }
  }
}

export const objectDiff = (object, base) => {
  return reduce(
    object,
    (result, value, key) => {
      if (!isEqualLd(value, base[key])) {
        let newValue = value;
        if (isPlainObject(value) && isPlainObject(base[key])) {
          newValue = objectDiff(value, base[key]);
        }
        if (!isPlainObject(newValue) || !isEmpty(newValue))
          //  eslint-disable-next-line no-param-reassign
          result[key] = newValue;
      }
      return result;
    },
    {},
  );
};
